// DS2 Modal Dialog: the native <dialog>, modal while mounted (the top layer: above everything, never clipped, the
// latest on top). onClose: the × and Esc call it and the parent unmounts the dialog (a click on the backdrop does
// nothing). title, preTitleButton / postTitleButton around it, buttons in the footer, fitContent (as wide as the
// content). Dragged by its header, kept inside the window (also when the window shrinks). On
// opening the dialog itself takes the focus (or an element marked data-autofocus), Tab cycles inside it, and on
// closing the focus goes back.
import cx from "classnames";
import {
  Fragment,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PropsWithChildren,
  type ReactNode,
  type Ref,
} from "react";
import type { ButtonElement } from "./Button";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import useI18n from "../../hooks/useTranslations";
import { mergeRefs } from "./mergeRefs";
import { CrossIcon } from "./icons";
import { ModalDialogContext } from "./modalDialogContext";
import styles from "./ModalDialog.module.css";

export type ModalDialogProps = PropsWithChildren<{
  onClose: () => void;
  preTitleButton?: ReactNode;
  postTitleButton?: ReactNode;
  title?: string;
  buttons?: ButtonElement | ButtonElement[];
  className?: string;
  fitContent?: boolean;
  // the <dialog>
  ref?: Ref<HTMLDialogElement>;
}>;

// the dialog at left / top, inside the window
function place(d: HTMLDialogElement, left: number, top: number) {
  const clamp = (v: number, max: number) => Math.max(0, Math.min(v, max));
  d.style.left = `${clamp(left, window.innerWidth - d.offsetWidth)}px`;
  d.style.top = `${clamp(top, window.innerHeight - d.offsetHeight)}px`;
}

export function ModalDialog({
  title,
  onClose,
  preTitleButton,
  postTitleButton,
  buttons,
  fitContent,
  className,
  children,
  ref: outerRef,
}: ModalDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  // the <dialog>, for what its content floats over it (ModalDialogContext)
  const [dialog, setDialog] = useState<HTMLDialogElement | null>(null);
  const titleId = useId();
  const trapFocus = useFocusTrap(ref);
  const closeLabel = useI18n().translation.designSystem.closeLabel;

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const before = document.activeElement as HTMLElement | null;
    if (!d.open) d.showModal();
    (d.querySelector<HTMLElement>("[data-autofocus]") ?? d).focus();
    return () => {
      d.close();
      before?.focus?.();
    };
  }, []);

  // once dragged, the window's shrinking keeps it inside
  useEffect(() => {
    const onResize = () => {
      const d = ref.current;
      if (d?.style.left) place(d, parseFloat(d.style.left), parseFloat(d.style.top));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // drag by the header (not by its buttons): from the centre to fixed left / top, the width kept
  const onDragStart = (e: ReactMouseEvent<HTMLDivElement>) => {
    const d = ref.current;
    if (!d || e.button !== 0 || (e.target as Element).closest("button, a, input, select, textarea")) return;
    e.preventDefault(); // no text selection while dragging
    const r = d.getBoundingClientRect();
    const dx = e.clientX - r.left;
    const dy = e.clientY - r.top;
    Object.assign(d.style, { width: `${r.width}px`, margin: "0", right: "auto", bottom: "auto" });
    place(d, r.left, r.top);
    const move = (ev: MouseEvent) => place(d, ev.clientX - dx, ev.clientY - dy);
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const hasHeader = title || preTitleButton || postTitleButton;
  return (
    <dialog
      ref={mergeRefs(ref, outerRef, setDialog)}
      className={cx(styles.dialog, fitContent && styles.fitContent, className)}
      aria-labelledby={title ? titleId : undefined}
      aria-modal="true"
      tabIndex={-1}
      onKeyDown={trapFocus}
      onCancel={(e) => {
        // Esc: the parent decides (it unmounts the dialog)
        e.preventDefault();
        onClose();
      }}
      // closed by the browser without a cancel event (a second Esc with no click in between): the parent learns it
      onClose={() => {
        if (ref.current && !ref.current.open) onClose();
      }}
    >
      <div className={cx(styles.header, !hasHeader && styles.bare)} onMouseDown={onDragStart}>
        {preTitleButton}
        {title ? (
          <h2 className={styles.title} id={titleId}>
            {title}
          </h2>
        ) : null}
        {postTitleButton}
        <button type="button" className={styles.close} aria-label={closeLabel} title={closeLabel} onClick={onClose}>
          <CrossIcon />
        </button>
      </div>
      <div className={styles.body}>
        <ModalDialogContext value={dialog}>{children}</ModalDialogContext>
      </div>
      {buttons ? (
        <div className={styles.footer}>
          {(Array.isArray(buttons) ? buttons : [buttons]).map((button, i) => (
            <Fragment key={i}>{button}</Fragment>
          ))}
        </div>
      ) : null}
    </dialog>
  );
}

export default ModalDialog;
